import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ErrorPage } from "@/components/ui/error-page";
import { mapSearchParamsToErrorPageSource } from "@/i18n/problem-detail";
import { SessionEndedState } from "@/components/shell/session-ended-state";
import { probeSessionEnded, referrerReturnPath, takeAutoRedirect } from "@/lib/session-expiry";

type SignInCheck =
  | { kind: "not-needed" }
  | { kind: "checking" }
  | { kind: "signed-in" }
  | { kind: "required"; returnTo: string; autoRedirect: boolean };

/**
 * /error — reached via the browser 302 redirect the Spring/backend half of
 * ALL-4857 issues on an auth failure:
 *   <spa origin>/error?code=<AuthReasonCode>&ref=<supportReference>
 * Also linkable directly (e.g. from a support macro) with the same params.
 */
export default function ErrorRoutePage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // ALL-5774 (founder login requirement #3: "the page isnt redirected automatically"): the
  // edge sends a browser that opened a page needing a session to /error?code=HTTP_401. When
  // GET /auth/session confirms there is no session, this is not an incident: show the
  // sign-in state and take the person to sign in on their own, back to the page that linked
  // here. The loop guard (one automatic redirect per 30s per tab) still applies. A live
  // session (a transient 401) keeps the normal error page. Only the edge's HTTP_401 does
  // this; a gateway AUTH_* code from a refused sign-in never redirects on its own, so a
  // failing sign-in cannot loop.
  const signInCheckNeeded = params.get("code") === "HTTP_401";
  const [signIn, setSignIn] = useState<SignInCheck>(
    signInCheckNeeded ? { kind: "checking" } : { kind: "not-needed" },
  );
  useEffect(() => {
    if (!signInCheckNeeded) return undefined;
    let cancelled = false;
    void probeSessionEnded().then((ended) => {
      if (cancelled) return;
      setSignIn(
        ended
          ? { kind: "required", returnTo: referrerReturnPath(), autoRedirect: takeAutoRedirect(Date.now()) }
          : { kind: "signed-in" },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [signInCheckNeeded]);

  if (signIn.kind === "checking") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg">
        <div className="spinner" aria-label="Checking session" />
      </main>
    );
  }
  if (signIn.kind === "required") {
    return <SessionEndedState reason="sign-in-required" returnTo={signIn.returnTo} autoRedirect={signIn.autoRedirect} />;
  }

  const source = mapSearchParamsToErrorPageSource(params, location.pathname);
  return <ErrorPage source={source} />;
}
